import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import { FormFieldsData, FormShowTemplate, TextArea, useFormContext, isEmptyFormErrors } from 'framework/form';

import { FormattedTextField, TextField } from 'framework/ui/field';
import { DATE_FORMAT, EMPTY_VALUE_INDICATOR, useDateService } from 'framework/utils';
import { ClientPortalRedmineRequest, ProjectClientPortalData } from '../types';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ValidationSchema } from 'validation-schema-library';
import { AccountLinkField } from 'domain/shared/account';
import { FormButtonsLayout } from 'framework/ui/layout';
import { Alert, Loading } from 'framework/ui/mui-builder';

type ApproveClientUsersDrawerContentProps = {
  onReviewed: () => void;
  approveClientUsers: (values: FormFieldsData) => Promise<object | void>;
  rejectClientUsers: (values: FormFieldsData) => Promise<object | void>;
  getRejectUserValidation: () => Promise<ValidationSchema>;
  projectData: ProjectClientPortalData;
  activeRequest?: ClientPortalRedmineRequest;
  isActiveRequestsLoading?: boolean;
};

const ApprovalActions = ({
  approveClientUsers,
  rejectClientUsers,
  onReviewed,
}: {
  approveClientUsers: (values: FormFieldsData) => Promise<object | void>;
  rejectClientUsers: (values: FormFieldsData) => Promise<object | void>;
  onReviewed: () => void;
}) => {
  const { data = {}, validateForm } = useFormContext();

  const handleApprove = async () => {
    await approveClientUsers(data);
    onReviewed();
  };
  const handleReject = async () => {
    const errors = validateForm?.();
    if (errors && !isEmptyFormErrors(errors)) {
      return;
    }
    await rejectClientUsers(data);
    onReviewed();
  };

  return (
    <FormButtonsLayout
      submitButtonText="Approve"
      onSubmitAction={handleApprove}
      onCancelAction={handleReject}
      cancelButtonText="Reject"
    />
  );
};

const ApproveClientUsersDrawerContent = ({
  approveClientUsers,
  rejectClientUsers,
  getRejectUserValidation,
  projectData,
  activeRequest,
  isActiveRequestsLoading,
  onReviewed,
}: ApproveClientUsersDrawerContentProps) => {
  const clients = (activeRequest?.requestedUsers || []).map(user => ({
    name: `${user.firstName} ${user.lastName}`.trim(),
    email: user.email,
    accessUntil: user.accessUntil,
  }));
  const { formatDate } = useDateService();

  if (isActiveRequestsLoading) {
    return <Loading size="large" />;
  } else if (!activeRequest) {
    return <Alert severity="error">Invite user request not found.</Alert>;
  }

  return (
    <FormShowTemplate
      initEditMode
      initialData={{ clients, decisionNote: '' }}
      getDataFunc={() => Promise.resolve({ clients, decisionNote: '' })}
      submitFormFunc={approveClientUsers}
      getValidationSchemaFunc={getRejectUserValidation}
    >
      <Stack spacing={2} pb={4}>
        <Typography color="textSecondary">
          Review the requested users before granting them access to the Client Portal. Once approved, the users will be
          automatically added to the portal and receive an invitation to access it.
        </Typography>
        <Stack spacing={1} pb={2}>
          <Typography variant="h6">Request Information</Typography>
          <Grid container spacing={1}>
            <Grid size={3}>
              <Typography fontWeight={600}>Project</Typography>
            </Grid>
            <Grid size={9}>{activeRequest?.projectName || projectData.name || EMPTY_VALUE_INDICATOR}</Grid>
            <Grid size={3}>
              <Typography fontWeight={600}>Account</Typography>
            </Grid>
            <Grid size={9}>
              <AccountLinkField
                value={activeRequest?.accountAlias || projectData.accountData?.accountName}
                idValue={activeRequest?.accountAlias || String(projectData.accountData?.accountId || '')}
              />
            </Grid>
            <Grid size={3}>
              <Typography fontWeight={600}>Requested by</Typography>
            </Grid>
            <Grid size={9}>
              <EmployeeLinkFieldWithNameLoading
                value={activeRequest?.submittedBy || activeRequest?.createdBy}
                label=""
              />
            </Grid>
            <Grid size={3}>
              <Typography fontWeight={600}>Requested on</Typography>
            </Grid>
            <Grid size={9}>
              {activeRequest?.submittedAt
                ? formatDate(activeRequest.submittedAt, DATE_FORMAT.API_ONLY_DATE)
                : EMPTY_VALUE_INDICATOR}
            </Grid>
          </Grid>
        </Stack>
        <Stack spacing={1} pb={2}>
          <Typography variant="h6">Client information</Typography>
          {clients.map((client, index) => (
            <Grid container direction="row" key={client.email || index}>
              <Grid size={3}>
                <TextField source={`clients.${index}.name`} label="" />
              </Grid>
              <Grid size={5}>
                <TextField source={`clients.${index}.email`} label="" />
              </Grid>
              <Grid size={4}>
                <FormattedTextField
                  source={`clients.${index}.accessUntil`}
                  label=""
                  format={(value: string) =>
                    value ? `until ${formatDate(value, DATE_FORMAT.FULL_DATE)}` : EMPTY_VALUE_INDICATOR
                  }
                />
              </Grid>
            </Grid>
          ))}
        </Stack>
        <TextArea source={'decisionNote'} label={'Decision note'} />

        <ApprovalActions
          approveClientUsers={approveClientUsers}
          rejectClientUsers={rejectClientUsers}
          onReviewed={onReviewed}
        />
      </Stack>
    </FormShowTemplate>
  );
};

export default ApproveClientUsersDrawerContent;
